import { FC, useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { ConsumptionIntervalModelType, SitesModelType, SitesSourceModelType } from "../../../../../common/modelTypes";
import { col6 } from "../../../../../common/utils";
import ComparisonLinearProgress from "../../../../../components/charts/graph/ComparisonLinearProgress";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import TableData from "../../../../../components/common/table/TableData";
import { OperationalUnitsGreenrouterTabConsumptions } from "../../../../../context/PageConfigrations/pageConfiguration.types";
import {
	ConsumptionIntervalResultsDTO,
	CountriesEmissionsResultsDTO,
	ExtendedLabeledValueDTO,
	NodesMapDTO,
} from "../../../../../datasources/api-client/data-contracts";
import { EmissionsTypesColors } from "../../../../../theme/themeValues";
import EmissionsIntervals from "../../../components/ResultsEmissionsIntervals";
import { MapBounds } from "../../useOperationalUnitsData";
import MapEmissionNodes from "./components/MapEmissionNodes";
import { dataColSitesSource, getColumnsSitesSource, getRowsSitesSource } from "./data";

export type EmissionConsumptionKey = keyof ConsumptionIntervalResultsDTO;

export type SourcesTable = {
	rows: { id: string | number; [key: string]: any }[];
	columns: GridColDef[];
};

export type MapsEmissionsNodesType = {
	nodes: {
		nodesData: NodesMapDTO | undefined;
		isLoading: boolean | undefined;
		reload: VoidFunction | undefined;
	};
	emissions: {
		emissionData: CountriesEmissionsResultsDTO | undefined;
		isLoading: boolean | undefined;
		reload: VoidFunction | undefined;
	};
};

interface Props {
	emissionsSites: SitesModelType;
	from: string;
	to: string;
	emissionsSitesSource: SitesSourceModelType;
	emissionsConsumptionIntervals: ConsumptionIntervalModelType;
	consumptionSources: { electricity: SourcesTable } | undefined;
	loadingSources: boolean;
	consumptionReload: VoidFunction;
	emissionsIntervalsPeriodType: "month" | "quarter" | "four-month" | "semester";
	setEmissionsIntervalsPeriodType: (periodType: "month" | "quarter" | "four-month" | "semester") => void;
	maps: MapsEmissionsNodesType;
	onBoundsChange: (bounds: MapBounds) => void;
	mapBounds: MapBounds | undefined;
	greenrouterSelectedTabConsumption: OperationalUnitsGreenrouterTabConsumptions;
	setGreenrouterSelectedTabConsumption: (tab: OperationalUnitsGreenrouterTabConsumptions) => void;
}

const Greenrouter: FC<Props> = ({
	emissionsSites,
	emissionsSitesSource,
	emissionsConsumptionIntervals,
	consumptionSources,
	loadingSources,
	consumptionReload,
	emissionsIntervalsPeriodType,
	setEmissionsIntervalsPeriodType,
	maps,
	onBoundsChange,
	mapBounds,
	greenrouterSelectedTabConsumption,
	setGreenrouterSelectedTabConsumption,
}) => {
	const { t } = useTranslation("feature.units");

	// Comparison data for operational units with/without consumption
	const fromEmissionsDatasource = emissionsSites && {
		name: t("overview/with consumption"),
		value: emissionsSites?.data?.greenRouter.current.countWithWtw.percentage,
	};
	const toEmissionsDatasource = emissionsSites && {
		name: t("overview/without consumption"),
		value: emissionsSites?.data?.greenRouter.current.countWithoutWtw.percentage,
	};

	// Memoize dropdown items
	const dropdownItems = useMemo(() => {
		const data = emissionsConsumptionIntervals.data;
		if (!data) return [];

		return (Object.keys(data) as OperationalUnitsGreenrouterTabConsumptions[]).map((key) => ({
			key,
			label: `${t(`greenrouter/consumption emission/${key}`)} ${data[key].measure}`,
		}));
	}, [emissionsConsumptionIntervals.data, t]);

	// Update greenrouterSelectedTabConsumption when dropdownItems change
	useEffect(() => {
		if (dropdownItems.length > 0 && !greenrouterSelectedTabConsumption) {
			setGreenrouterSelectedTabConsumption(dropdownItems[0].key);
		}
	}, [dropdownItems, greenrouterSelectedTabConsumption]);

	// Handle the change of graphics tab and store it to local storage
	const handleSelectKeyChange = (value: OperationalUnitsGreenrouterTabConsumptions | string) => {
		setGreenrouterSelectedTabConsumption(value as OperationalUnitsGreenrouterTabConsumptions);
	};

	// Compute chart data and measure for the selected interval.
	const { chartData, chartMeasure } = useMemo(() => {
		if (!emissionsConsumptionIntervals.data || !greenrouterSelectedTabConsumption) {
			return { chartData: [], chartMeasure: "" };
		}

		// Data for the selected category
		const interval = emissionsConsumptionIntervals.data[greenrouterSelectedTabConsumption];

		if (!interval?.data?.length) {
			return { chartData: [], chartMeasure: interval?.measure ?? "" };
		}

		const base = interval.data;

		const chartData = base.map((item: ExtendedLabeledValueDTO) => {
			const result: any = {
				label: item.label,
				value: item.value,
			};

			if (Array.isArray(item.details)) {
				item.details.forEach((detail: ExtendedLabeledValueDTO) => {
					result[detail.label] = detail.value;
				});
			}

			return result;
		});

		return {
			chartData,
			chartMeasure: interval.measure ?? "",
		};
	}, [emissionsConsumptionIntervals, greenrouterSelectedTabConsumption]);

	// Assign chart colors based on emission type
	const chartColors: Record<EmissionConsumptionKey, string> = {
		electricity: EmissionsTypesColors.LightElectricity,
		fuel: EmissionsTypesColors.LightFuel,
		heatSteam: EmissionsTypesColors.LightHeat,
		refrigerants: EmissionsTypesColors.LightRefrigerant,
		processGas: EmissionsTypesColors.LightProcessGases,
		water: EmissionsTypesColors.LightWater,
	};
	const chartColor = greenrouterSelectedTabConsumption && chartColors[greenrouterSelectedTabConsumption];

	// Determine source emission table for the selected key
	const sourceEmission =
		greenrouterSelectedTabConsumption && consumptionSources
			? consumptionSources[greenrouterSelectedTabConsumption as keyof typeof consumptionSources]
			: undefined;

	const marketBase: MarketBaseType = {
		emissions: emissionsSites.data?.greenRouter?.current.marketBasedWtw,
		delta: emissionsSites.data?.greenRouter?.current.marketBasedWtwDelta,
		coverage: null,
		isLoading: emissionsSites.isLoading,
		onReload: emissionsSites.reload,
	};

	return (
		<Grid container spacing={2}>
			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("overview/total emissions")}
					value={emissionsSites?.data?.greenRouter.current.co2Wtw.value}
					delta={emissionsSites?.data?.greenRouter.delta.co2Wtw}
					measure={emissionsSites?.data?.greenRouter.current.co2Wtw.measure}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
				/>
			</Grid>
			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("overview/no operational units")}
					value={emissionsSites?.data?.greenRouter.current.count}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
					graph={<ComparisonLinearProgress from={fromEmissionsDatasource} to={toEmissionsDatasource} />}
				/>
			</Grid>

			<Grid size={col6} minHeight={400}>
				<MapEmissionNodes maps={maps} onBoundsChange={onBoundsChange} mapBounds={mapBounds} />
			</Grid>
			<Grid size={col6} minHeight={400}>
				<TableData
					columns={getColumnsSitesSource(dataColSitesSource, t)}
					rows={emissionsSitesSource.data ? getRowsSitesSource(emissionsSitesSource.data, t) : []}
					title={t("greenrouter/emissions and consumption by source")}
					isLoading={emissionsSitesSource.isLoading}
					onReload={emissionsSitesSource.reload}
				/>
			</Grid>
			<Grid size={12} minHeight={400}>
				<EmissionsIntervals
					mode="tabs"
					selectTitle={t("greenrouter/consumption trends")}
					onReload={emissionsConsumptionIntervals.reload}
					isLoading={emissionsConsumptionIntervals.isLoading && loadingSources}
					menuOptions={dropdownItems}
					selectedKey={greenrouterSelectedTabConsumption}
					handleSelectKeyChange={handleSelectKeyChange}
					chartData={chartData}
					chartMeasure={chartMeasure}
					chartColor={chartColor}
					titleSourceEmission={t("greenrouter/consumption trends and details")}
					sourceEmission={sourceEmission}
					consumptionReload={consumptionReload}
					periodType={emissionsIntervalsPeriodType}
					setPeriodType={setEmissionsIntervalsPeriodType}
				/>
			</Grid>

			<AmbientalKpiWidget
				bio={emissionsSites.data?.greenRouter.current.biogenic}
				loading={emissionsSites.isLoading}
				onReload={emissionsSites.reload}
			/>

			<MarketBaseWidget marketBase={marketBase} />
		</Grid>
	);
};

export default Greenrouter;
